import { Controller } from "@hotwired/stimulus"
import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { labelFontSize, labelFontWeight, resolvedSegmentPercentage } from "../lib/chart_colors.js"

// Two modes, switched on whether labels/data values are present (card #3613
// round 9):
//   - Percentage mode (the original, unchanged): a two-slice compliant/
//     remainder ring for a percentage-unit result. Its label is an HTML
//     overlay (_results_summary.html.erb), not canvas-drawn, so datalabels
//     stays off here -- see renderPercentage.
//   - Distribution mode: a multi-slice ring from a mission task's
//     response_distribution, mirroring chart_preview_controller.js's
//     renderDistributionChart at small-card scale. Round 12 wired up
//     per-segment datalabels here -- previously this branch drew no label of
//     any kind, the regression Jay reported.
export default class extends Controller {
  static values = {
    percentage: Number,
    count: Number,
    primaryColor: { type: String, default: "#003d68" },
    secondaryColor: { type: String, default: "#e5e7eb" },
    chartKind: { type: String, default: "doughnut" },
    labelStyle: { type: String, default: "percentage" },
    labels: { type: Array, default: [] },
    data: { type: Array, default: [] },
    colors: { type: Array, default: [] },
    denominator: { type: Number, default: 0 },
    labelColor: { type: String, default: "#003d68" },
    labelSize: { type: String, default: "medium" },
    labelWeight: { type: String, default: "bold" },
    progress: { type: Boolean, default: false }
  }

  connect() {
    this.initializeChart()
  }

  // Card #4273: a Turbo Stream re-render can leave the stale <canvas> in
  // place while this controller reconnects -- without destroying the old
  // Chart.js instance first, a second instance attaches to the same canvas
  // and stacks on top of the first (overlapping/crossed arcs, stray digits).
  disconnect() {
    this.destroyChart()
  }

  destroyChart() {
    this.chart?.destroy()
    this.chart = null
  }

  get isDistribution() {
    return this.labelsValue.length > 0
  }

  initializeChart() {
    // Defensive: destroy any instance already attached to this canvas before
    // creating a new one (belt-and-braces alongside disconnect()).
    this.destroyChart()

    const ctx = this.element.getContext('2d')

    if (this.isDistribution) {
      this.renderDistribution(ctx)
    } else {
      this.renderPercentage(ctx)
    }
  }

  renderPercentage(ctx) {
    const isPie = this.chartKindValue === "pie"
    // The arc clamps to a full ring; the percentage text beside it stays uncapped.
    // Without this, a result above 100% draws a negative remainder slice (ADR-0126).
    const arcPercentage = Math.min(Math.max(this.percentageValue, 0), 100)

    this.chart = new Chart(ctx, {
      type: isPie ? 'pie' : 'doughnut',
      data: {
        datasets: [{
          data: [arcPercentage, 100 - arcPercentage],
          backgroundColor: [this.primaryColorValue, this.secondaryColorValue],
          borderWidth: 0
        }]
      },
      options: {
        cutout: isPie ? '0%' : '70%',
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
          datalabels: { display: false }
        },
        elements: {
          arc: {
            borderWidth: 0
          }
        },
        responsive: false,
        maintainAspectRatio: true,
        animation: {
          animateRotate: true,
          duration: 800
        }
      }
    })
  }

  renderDistribution(ctx) {
    const isPie = this.chartKindValue === "pie"
    const labels = this.labelsValue
    const data = this.dataValue
    const labelStyle = this.labelStyleValue
    const denominator = this.denominatorValue
    const progress = this.progressValue

    this.chart = new Chart(ctx, {
      type: isPie ? 'pie' : 'doughnut',
      plugins: [ChartDataLabels],
      data: {
        labels: labels,
        datasets: [{
          data: data,
          backgroundColor: this.colorsValue.length === data.length
            ? this.colorsValue
            : Array.from({ length: data.length }, () => this.primaryColorValue),
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        cutout: isPie ? '0%' : '50%',
        responsive: false,
        maintainAspectRatio: true,
        // Fixed 128x128 canvas (_results_summary.html.erb, _chart_display.html.erb):
        // at Large label size a segment's datalabel text can be wider than the
        // margin between its mid-ring anchor and the true canvas edge, so the
        // text itself (not just its anchor point) draws outside the canvas and
        // gets hard-clipped there -- clamp alone doesn't prevent this since it
        // only keeps the ANCHOR point inside the chart area, it has no concept
        // of the label's own rendered width. Padding shrinks the ring slightly,
        // pulling every segment's anchor further from the true edge so there's
        // room for the label box too.
        //
        // Round 20 (card #3613): Round 14's padding of 24 was NOT enough --
        // confirmed by a real pixel screenshot (not a fillText intercept) on
        // task 59947's real "12 (60%)"/"8 (40%)" split, Label = Both: at
        // Large label size the text's own rightmost pixel column landed
        // exactly on the canvas's last pixel column (x=127 of 128) -- true
        // hard clipping, not just a tight fit. anchor/align are already
        // 'center' by the datalabels plugin's own default (confirmed by
        // reading its source) -- explicitly re-setting them would change
        // nothing, so padding is the one real lever here, per the brief's
        // own least-disruption-first ordering.
        //
        // Raised to 34 (not 40 -- tried both, screenshotted both): 34 gives a
        // clean 5-18px margin from the true edge across small/medium/large
        // (re-verified with the same screenshot technique, see the round 20
        // build report) without shrinking the ring any further than
        // necessary.
        //
        // Card #4273: anchor/align were previously left at the datalabels
        // default of 'center', which places every segment's label at the
        // MIDPOINT of its own arc -- for a near-symmetric split (e.g. a
        // 3-option checkbox breakdown, or a 60/40 two-slice split) those
        // midpoints land close enough together near the ring's centre that
        // wide labels (Large size especially) visually collide. Anchoring
        // to 'end' (the arc's outer radius) and aligning 'end' (offsetting
        // further outward from there) spreads each label out along its own
        // slice's radial line instead of converging toward the donut hole,
        // which is what actually separates them for 3+ segment breakdowns.
        // clamp (below) then pulls anything that lands outside the padded
        // canvas back in, so this doesn't reopen the edge-clipping defect
        // the padding above was raised to fix.
        layout: { padding: 34 },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
          datalabels: {
            // The configured label colour always wins here -- Chart.js's
            // datalabels plugin doesn't need a per-segment contrast fallback
            // the way a bar's inside-fill label does, and per-segment
            // contrast-matching would fight a user's explicit colour choice.
            color: this.labelColorValue,
            // Pushes each label toward its own slice's outer edge rather
            // than the arc midpoint -- see the centre-collision comment
            // above (card #4273).
            anchor: 'end',
            align: 'end',
            // A segment's label anchor can sit right at the canvas edge for a
            // slice on that side of the ring -- clamp keeps it inside the
            // chart area instead of hard-clipping (same fix as
            // bar_chart_controller.js).
            clamp: true,
            font: { weight: labelFontWeight(this.labelWeightValue), size: labelFontSize(this.labelSizeValue) },
            formatter: (value, context) => {
              const total = context.dataset.data.reduce((a, b) => a + b, 0)
              const chartOwnPct = total > 0 ? Math.round(value / total * 100) : 0

              if (labelStyle === 'none') return ''
              if (labelStyle === 'count') return progress ? `${value} of ${denominator}` : value
              if (labelStyle === 'both') return `${value} (${resolvedSegmentPercentage(value, total, denominator)}%)`
              if (labelStyle === 'percentage_of_responses') {
                return `${resolvedSegmentPercentage(value, total, denominator)}%`
              }
              // 'percentage' (default) and any unrecognised style
              return `${chartOwnPct}%`
            }
          }
        },
        animation: {
          animateRotate: true,
          duration: 800
        }
      }
    })
  }
}
